import axios from 'axios';

// Em produção usar URL relativa (/api) para o mesmo host — evita 404 quando proxy ou domínio difere
const isProduction = process.env.NODE_ENV === 'production';
const API_URL = isProduction
  ? (process.env.REACT_APP_API_URL || '/api')
  : (process.env.REACT_APP_API_URL || 'http://localhost:3005/api');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para 401: desloga e redireciona para login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('cliente');
      const path = window.location.pathname || '';
      if (!path.startsWith('/admin')) {
        window.location.href = '/login';
      } else {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Normalizar resposta que pode ser array ou { data, total }
const toArray = (res) => {
  const d = res.data;
  const list = Array.isArray(d) ? d : (d?.data || []);
  return { ...res, data: list };
};

// Produtos (params opcional: { categoria_id, subcategoria_id, limit } para filtro e carrossel)
export const getProdutos = (params) => api.get('/produtos', { params: params || {} }).then(toArray);
export const getProduto = (id) => api.get(`/produtos/${id}`);
export const createProduto = (formData) => api.post('/produtos', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const updateProduto = (id, formData) => api.put(`/produtos/${id}`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteProduto = (id) => api.delete(`/produtos/${id}`);

// Cores do produto (subcategoria coloração) — cadastro vinculado ao id do produto
export const getProdutoCores = (produtoId) => api.get(`/produtos/${Number(produtoId)}/cores`);
// FormData: remover Content-Type para o navegador enviar multipart/form-data com boundary (foto sobe para uploads/)
export const addProdutoCor = (produtoId, formData) =>
  api.post(`/produtos/${Number(produtoId)}/cores`, formData, {
    headers: { 'Content-Type': false },
    transformRequest: [(data, headers) => {
      if (data instanceof FormData) delete headers['Content-Type'];
      return data;
    }]
  });
export const updateProdutoCoresOrder = (produtoId, ids) => api.put(`/produtos/${Number(produtoId)}/cores/ordem`, { ids });
export const deleteProdutoCor = (produtoId, corId) => api.delete(`/produtos/${Number(produtoId)}/cores/${corId}`);

// Clientes
export const getClientes = () => api.get('/clientes');
export const getCliente = (id) => api.get(`/clientes/${id}`);
export const registerCliente = (data) => api.post('/clientes/registro', data);
export const loginCliente = (data) => api.post('/clientes/login', data);
export const loginAdmin = (data) => api.post('/clientes/admin/login', data);
export const updateCliente = (id, data) => api.put(`/clientes/${id}`, data);

// Avaliações
export const getAvaliacoes = (produtoId) => api.get(`/avaliacoes/produto/${produtoId}`);
export const createAvaliacao = (data) => api.post('/avaliacoes', data);
export const getAvaliacaoMedia = (produtoId) => api.get(`/avaliacoes/media/${produtoId}`);

// Comentários
export const getComentarios = (produtoId) => api.get(`/comentarios/produto/${produtoId}`);
export const createComentario = (data) => api.post('/comentarios', data);
export const deleteComentario = (id) => api.delete(`/comentarios/${id}`);

// Estoque
export const getEstoque = () => api.get('/estoque');
export const getEstoqueProduto = (produtoId) => api.get(`/estoque/produto/${produtoId}`);
export const updateEstoque = (produtoId, data) => api.put(`/estoque/produto/${produtoId}`, data);
export const getMovimentacoesEstoque = (params) => api.get('/estoque/movimentacoes', { params: params || {} });
export const ajustarEstoqueLote = (ajustes) => api.post('/estoque/ajuste-lote', { ajustes });

// Categorias (sempre retorna response.data = array)
export const getCategorias = () => api.get('/categorias').then(toArray);
export const getCategoria = (id) => api.get(`/categorias/${id}`);
export const createCategoria = (data) => api.post('/categorias', data);
export const updateCategoria = (id, data) => api.put(`/categorias/${id}`, data);
export const deleteCategoria = (id) => api.delete(`/categorias/${id}`);

// Subcategorias
export const getSubcategorias = () => api.get('/subcategorias');
export const getSubcategoriasByCategoria = (categoriaId) => api.get(`/subcategorias/categoria/${categoriaId}`);
export const createSubcategoria = (data) => api.post('/subcategorias', data);
export const updateSubcategoria = (id, data) => api.put(`/subcategorias/${id}`, data);
export const deleteSubcategoria = (id) => api.delete(`/subcategorias/${id}`);

// Configurações prazo (público: checkout; admin: edição)
export const getPrazoConfig = () => api.get('/configuracoes/prazo');
export const updatePrazoConfig = (config) => api.put('/configuracoes/prazo', config);
// Legado (compatibilidade)
export const getJurosPrazo = () => api.get('/configuracoes/juros-prazo');
export const updateJurosPrazo = (percentual) => api.put('/configuracoes/juros-prazo', { juros_prazo_percentual: percentual });

// Pedidos (cliente logado)
export const getPedidos = () => api.get('/pedidos');
export const getPedido = (id) => api.get(`/pedidos/${id}`);
export const getPedidosAdmin = () => api.get('/pedidos/admin');
export const getPedidosAdminStats = (params) => api.get('/pedidos/admin/stats', { params: params || {} });
export const createPedido = (data) => api.post('/pedidos', data);
export const createPedidoAdmin = (data) => api.post('/pedidos/admin', data);
export const updatePedidoStatus = (id, status) => api.patch(`/pedidos/${id}`, { status });
export const solicitarCancelamentoPedido = (id) => api.patch(`/pedidos/${id}/solicitar-cancelamento`);

// Area A Prazo (admin)
export const getPrazoClientesAdmin = () => api.get('/aprazo/clientes');
export const createPrazoClienteAdmin = (data) => api.post('/aprazo/clientes', data);
export const updatePrazoClienteAdmin = async (id, data) => {
  const path = `/aprazo/clientes/${id}`;
  try {
    return await api.put(path, data);
  } catch (err) {
    const status = Number(err?.response?.status || 0);
    if (status !== 404 && status !== 405) throw err;
  }
  try {
    return await api.patch(path, data);
  } catch (err) {
    const status = Number(err?.response?.status || 0);
    if (status !== 404 && status !== 405) throw err;
  }
  return api.post(path, data);
};
export const getPrazoVendasAdmin = (params) => api.get('/aprazo/vendas', { params: params || {} });
export const getPrazoAdminStats = (params) => api.get('/aprazo/admin/stats', { params: params || {} });
export const createPrazoVendaAdmin = (data) => api.post('/aprazo/vendas', data);
export const createPrazoPagamentoAdmin = (vendaId, data) => api.post(`/aprazo/vendas/${vendaId}/pagamentos`, data);
export const deletePrazoVendaAdmin = (vendaId) => api.delete(`/aprazo/vendas/${vendaId}`);
export const reopenPrazoVendaAdmin = (vendaId) => api.post(`/aprazo/vendas/${vendaId}/reabrir`);
export const addPrazoVendaItemAdmin = (vendaId, data) => api.post(`/aprazo/vendas/${vendaId}/itens`, data);

// Mensagens (cliente logado)
export const getMensagens = () => api.get('/mensagens');
export const marcarMensagemLida = (id) => api.patch(`/mensagens/${id}/lida`);

// Notificações de promoção (cliente logado)
export const getNotificacoes = () => api.get('/notificacoes');
export const marcarNotificacaoLida = (id) => api.patch(`/notificacoes/${id}/lida`);

// Carrossel inicial (banco de dados) — público e admin
export const getCarouselInicial = () => api.get('/carousel-inicial');
export const saveCarouselInicial = (config) => api.put('/carousel-inicial', config);

// Carrossel inicial — upload de imagem (após recorte).
// Primeiro tenta /upload-carousel (rota no app principal); depois fallbacks.
const CAROUSEL_UPLOAD_PATHS = [
  '/upload-carousel',               // POST /api/upload-carousel (definida em index.js)
  '/upload/carousel',
  '/produtos/carousel-upload',
  '/configuracoes/carousel-upload',
  '/carousel/upload'
];

// Verificar se a API está acessível (útil para diagnosticar 404 por proxy)
export const checkApiHealth = () =>
  api.get('/health').then(() => true).catch((err) => {
    if (err.response?.status === 404 || err.code === 'ERR_NETWORK') return false;
    return true; // outros erros (401, 500) = API respondeu
  });

export const uploadCarouselImage = async (blobOrFile) => {
  const form = new FormData();
  const name = blobOrFile instanceof File ? blobOrFile.name || 'carousel.jpg' : 'carousel.jpg';
  form.append('image', blobOrFile, name);
  // Remover Content-Type para o navegador definir multipart/form-data com boundary (senão o axios usa application/json e o servidor não recebe o arquivo)
  const opts = { headers: { 'Content-Type': false } };
  let lastError;
  for (const uploadPath of CAROUSEL_UPLOAD_PATHS) {
    try {
      const res = await api.post(uploadPath, form, opts);
      return res;
    } catch (err) {
      lastError = err;
      if (err.response?.status === 404) continue;
      throw err;
    }
  }
  throw lastError;
};

export default api;

